import isEmpty from 'lodash/isEmpty';
import { isDevelopment } from '@v4/environment';

/**
 * Sends a hit to Google Analytics
 *
 * @param {string} category
 * @param {string} action
 * @param {?string} label
 * @param {?number} value
 * @param {object=} fieldsObject
 * @see https://developers.google.com/analytics/devguides/collection/analyticsjs/command-queue-reference#send
 * @returns {void}
 */
export const pushGaEvent = (category, action, label = null, value = null, fieldsObject = {}) => {
    if (!category || !action || isDevelopment()) {
        return;
    }

    let parsedValue = null;

    if (!isNaN(parseFloat(value)) && isFinite(value)) {
        parsedValue = parseInt(value, 10);
    }

    const ga = window.ga;

    if (ga && typeof ga === 'function') {
        const data = ['send', 'event', category, action, label, parsedValue];
        if (!isEmpty(fieldsObject)) {
            data.push(fieldsObject);
        }

        ga(...data);
    }
};

/**
 * Helper function used to extract extra data for the event
 * (data originating from BE) to be pushed to GA
 *
 * @param {string} eventLabel
 * @param {string|number} eventId
 * @param {object} eventsDataObject
 * @returns {object}
 */
const extractEventData = (eventLabel, eventId, eventsDataObject = {}) => {
    let eventWrapper = null;
    let eventFullName = '';

    // Construct the event full name for a match in the eventsDataObject
    eventFullName = eventId ? `${eventLabel}_${eventId}` : eventLabel;

    // Get the event wrapper from the eventsDataObject
    eventWrapper = eventsDataObject[eventFullName];

    // Extract the specific event data
    return eventWrapper ? eventWrapper.data : {};
};

/**
 * Sends a hit to Google Analytics
 * using gtag function
 *
 * @param {string} eventLabel
 * @param {string} eventId
 * @param {object} eventsDataObject
 * @param {object} eventParams
 * @returns {void}
 */
export const executePushGtagEvent = (eventLabel, eventId, eventsDataObject, eventParams) => {
    const gtag = window.gtag;

    if (!eventLabel || !gtag || isDevelopment()) {
        return;
    }

    try {
        let eventData = {};

        if (eventsDataObject) {
            eventData = extractEventData(eventLabel, eventId, eventsDataObject);
        }

        // Add the SiteSpec campaign ID, if present
        let kameleoonId = undefined;

        if (window && window.Kameleoon) {
            try {
                kameleoonId = window.Kameleoon.API.CurrentVisit.activatedExperiments[0].associatedVariationID;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn('[Kameleoon] Error getting Kameleoon associated variation id');
            }
        }

        // Create the final event data object to be sent to GA,
        // with the event parameters
        eventData = {
            ...eventData,
            ...eventParams,
            // eslint-disable-next-line camelcase
            ...(kameleoonId ? { kameleoon_experiment_id: kameleoonId } : {}),
        };

        const gtag = window.gtag;

        if (gtag && typeof gtag === 'function') {
            const data = ['event', eventLabel, eventData];

            gtag(...data);
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log(`GA4: Error when pushing GA event for ${eventLabel}`);
        // eslint-disable-next-line no-console
        console.log(error);
    }
};

// Check and re-check if gtag is loaded until it is.
// Stops checking after 10 attepts and fails.
// In some cases gtag is not loaded when the first event is triggered.
const checkIfGtagLoaded = () =>
    new Promise((resolve, reject) => {
        let checkCount = 0;

        const _isLoaded = function() {
            if (checkCount > 9) {
                reject('Error: gtag function undefined after 10 attempts');
                return;
            }

            if (window.gtag) {
                resolve();
                return;
            } else {
                checkCount = checkCount + 1;
                setTimeout(_isLoaded, 1000);
            }
        };

        _isLoaded();
    });

/**
 * Sends a hit to Google Analytics
 * using gtag function
 *
 * Wrapper function for "executePushGtagEvent"
 * which gets executed only after gtag function is found
 * using "checkIfGtagLoaded" promise resolves.
 *
 * @param {string} eventLabel
 * @param {string} eventId
 * @param {object} eventsDataObject
 * @param {object} eventParams
 * @returns {void}
 */
export const pushGtagEvent = (eventLabel, eventId = null, eventsDataObject = null, eventParams = {}) => {
    checkIfGtagLoaded()
        .then(() => {
            executePushGtagEvent(eventLabel, eventId, eventsDataObject, eventParams);
        })
        .catch();
};
